/* eslint-disable react/jsx-one-expression-per-line */
import {
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Spinner,
  Flex,
} from '@chakra-ui/core';
import React, { useEffect, useState } from 'react';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { ValidationOperator } from '../formBuilder/builderElements/formElements/questionValidationRules/questionValidationRules.types';
import { SCREENING_TYPE_LABELS } from '../questionnaireTemplate.constants';
import { QUESTIONNAIRE_ELEMENT_TYPE, SCREENING_TYPE } from '../questionnaireTemplate.enums';
import {
  DenormalizedElement,
  QuestionnaireTemplateResponse,
  QuestionnaireTemplateListResponse,
} from '../questionnaireTemplate.types';
import QuestionBody from './components/QuestionBody';
import QuestionContainer from './components/QuestionContainer';
import QuestionHeader from './components/QuestionHeader';
import QuestionImages from './components/QuestionImage';
import ValidationRuleBox from './components/ValidationRuleBox';

interface QuestionnaireTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: QuestionnaireTemplateListResponse[];
  finalFocusRef?: React.RefObject<any>;
  returnFocusOnClose?: boolean;
}

const getValidationMessage = (question: DenormalizedElement, t: TFunction<'questionnaireTemplate'>): React.ReactNode => {
  if (question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE) {
    if (question.maximumVideoDuration) {
      return (
        <>
          {t('formBuilder.canvas.element.validationRules.videoResponse.maxVideoLength')}{' '}
          <strong>{question.maximumVideoDuration}</strong>{' '}
          {t('formBuilder.canvas.element.validationRules.videoResponse.minLabel')}
        </>
      );
    }
    return null;
  }

  if (question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.DOCUMENT_UPLOAD) {
    if (question.maximumDocumentCount) {
      return (
        <>
          {t('formBuilder.canvas.element.documentUpload.validationRuleText1')} <strong>{question.maximumDocumentCount}</strong>{' '}
          {t('formBuilder.canvas.element.documentUpload.validationRuleText2')}
        </>
      );
    }
    return null;
  }

  const preferredOptions = (question.options || [])
    .map((option, index) => ({
      id: option.id,
      letter: String.fromCharCode(65 + index),
    }))
    .filter((o) => question.preferredAnswers?.includes(o.id))
    .map((o) => o.letter);

  if (!preferredOptions.length) return null;

  // Handle "any" logic
  const totalRaw = question.totalAnswer;
  const totalVal =
    !totalRaw || totalRaw === 'any'
      ? t('formBuilder.canvas.element.validationRules.any') || /* istanbul ignore next */ 'any'
      : totalRaw;

  // Pluralize the first "option/options" based on totalVal
  const isTotalPlural = totalVal !== 1 && totalVal !== '1';
  const totalOptionLabel = isTotalPlural ? t('validationMessages.options') : t('validationMessages.option');

  // Separate the "Options:" label from the actual letters so we can bold just the letters
  const optionLabel = preferredOptions.length > 1 ? t('validationMessages.options') : t('validationMessages.option');
  const letters = preferredOptions.join(', ');

  const translationValues = {
    total: totalVal,
    totalOptionLabel,
    optionLabel,
    letters,
  };

  const components = { bold: <Text as="span" fontWeight="bold" /> };

  switch (question.correctAnswerIf) {
    case ValidationOperator.IS_EQUAL:
      return <Trans i18nKey="validationMessages.isEqual" t={t} values={translationValues} components={components} />;

    case ValidationOperator.HAS_ANSWER:
      return <Trans i18nKey="validationMessages.hasAnswer" t={t} values={translationValues} components={components} />;

    case ValidationOperator.DOES_NOT_HAVE_ANSWER:
      return <Trans i18nKey="validationMessages.doesNotHaveAnswer" t={t} values={translationValues} components={components} />;

    case ValidationOperator.AT_LEAST:
      return (
        <Trans
          i18nKey="validationMessages.atLeast"
          t={t}
          values={{
            ...translationValues,
            min: question.shouldMatchAtLeast || 1,
          }}
          components={components}
        />
      );

    default:
      return null;
  }
};
const deriveScreeningType = (element: DenormalizedElement): SCREENING_TYPE => {
  if (element.knockout) return SCREENING_TYPE.KNOKOUT;
  if (element.isPausable) return SCREENING_TYPE.REVIEW;
  return SCREENING_TYPE.NONE;
};

const renderQuestion = (
  question: DenormalizedElement,
  level: number,
  questionNumber: string,
  t: TFunction<'questionnaireTemplate'>,
) => {
  const validationMessage = getValidationMessage(question, t);
  const shouldShowDivider =
    question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM ||
    question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO ||
    question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE;

  return (
    <React.Fragment key={question.id}>
      <QuestionContainer
        level={level}
        header={
          <QuestionHeader
            question={question.question}
            questionNumber={questionNumber}
            isKnockOut={question.knockout}
            type={question.questionType}
          />
        }
      >
        {question.images && <QuestionImages images={question.images} />}
        <QuestionBody question={question} />
        {question.followups?.map((child, index) => renderQuestion(child, level + 1, `${questionNumber}.${index + 1}`, t))}
        {shouldShowDivider && <Divider mt={2} borderColor="brand_200" />}
        {validationMessage && <ValidationRuleBox message={validationMessage} />}
        <VStack align="stretch" spacing={1} mt={3}>
          <Text fontSize="14px" color="neutralGray.700">
            {t('modal.screeningType')}: {SCREENING_TYPE_LABELS(t)[deriveScreeningType(question)]}
          </Text>
          {(question.shouldReaskOnReapply || question.isSkippable) && (
            <HStack spacing={2} align="center" wrap="wrap">
              {question.shouldReaskOnReapply && (
                <Text fontSize="14px" fontStyle="italic" color="#718096">
                  {t('modal.requireResponseAllApplications')}
                </Text>
              )}
              {question.isSkippable && (
                <Text fontSize="14px" fontStyle="italic" color="#718096">
                  {t('modal.candidatesCanSkip')}
                </Text>
              )}
            </HStack>
          )}
        </VStack>
      </QuestionContainer>
    </React.Fragment>
  );
};

export const QuestionnaireTemplateModal: React.FC<QuestionnaireTemplateModalProps> = ({
  isOpen,
  onClose,
  templates,
  finalFocusRef,
  returnFocusOnClose = true,
}) => {
  const { t } = useTranslation('questionnaireTemplate');

  const accountId = useStoreState((s) => s.app.user?.account);
  const getQuestionTemplateById = useStoreActions((a) => a.account.getQuestionTemplateById);

  const [templatesWithQuestions, setTemplatesWithQuestions] = useState<QuestionnaireTemplateResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (isOpen && templates.length > 0 && accountId) {
      const fetchAll = async () => {
        setIsLoading(true);
        try {
          const fetched = await Promise.all(
            templates.map((tmpl) => getQuestionTemplateById({ accountId, id: tmpl.id }).then((res) => res?.data)),
          );
          if (active) setTemplatesWithQuestions(fetched);
        } finally {
          if (active) setIsLoading(false);
        }
      };

      fetchAll().catch((err) => {
        console.error('Failed to fetch templates:', err);
        /* istanbul ignore next */ if (active) setIsLoading(false);
      });
    } else {
      setTemplatesWithQuestions([]);
    }
    return () => {
      active = false;
    };
  }, [isOpen, templates, accountId, getQuestionTemplateById]);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      size="xl"
      finalFocusRef={finalFocusRef}
      returnFocusOnClose={returnFocusOnClose}
    >
      <ModalOverlay />
      <ModalContent borderRadius="lg" boxShadow="xl" maxH="80vh" maxW="60vw">
        <ModalHeader>
          <HStack spacing={3} align="center">
            <Text fontSize="lg" fontWeight="bold">
              {templatesWithQuestions[0]?.name}
            </Text>
          </HStack>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pb={6}>
          {isLoading ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : (
            <VStack spacing={3} align="stretch">
              {templatesWithQuestions?.map((template) =>
                template.questions?.map((q, qIndex) => renderQuestion(q, 0, `${qIndex + 1}`, t)),
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
